import { useState } from 'react';
import { App as AntApp, Button, Card, Col, Row, Segmented, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PeriodFilter, presetToPeriod, type Period, type PresetKey } from '../components/period-filter';
import { KpiCard } from '../components/kpi-card';
import {
  CashFlowChart, CollectionsChart, InventoryPie, RevenueTrendChart, SalesFunnelChart,
} from '../components/charts';
import {
  useCashFlowStatement, useCollectionsReport, useInventoryReport, useOwnerSummary,
  useRevenueTrend, useSalesReport,
} from '../api/owner';
import { useSalesFunnel, useProjectProfitability } from '@/features/reports/api/reports';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';
import { downloadFromApi } from '@/shared/lib/download';

function qs(params: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) u.set(k, v);
  return u.toString();
}

// Tabs whose data is driven by the selected period. Snapshot/all-time tabs
// (inventory, projects) hide the period filter so it never looks broken.
const PERIOD_TABS = new Set(['overview', 'cashflow', 'sales', 'collections']);

/** Number of months a period spans (inclusive), clamped to 1..36. */
function monthsFromPeriod(p: Period): number {
  const f = new Date(p.from);
  const to = new Date(p.to);
  const m = (to.getFullYear() - f.getFullYear()) * 12 + (to.getMonth() - f.getMonth()) + 1;
  return Math.max(1, Math.min(36, m));
}

export function OwnerDashboardPage() {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<PresetKey>('thisMonth');
  const [period, setPeriod] = useState<Period>(() => presetToPeriod('thisMonth')!);
  const [tab, setTab] = useState('overview');

  function onPreset(key: PresetKey) {
    setPreset(key);
    const p = presetToPeriod(key);
    if (p) setPeriod(p);
  }

  const tabs = [
    { value: 'overview', label: t('owner.tabs.overview') },
    { value: 'cashflow', label: t('owner.tabs.cashflow') },
    { value: 'sales', label: t('owner.tabs.sales') },
    { value: 'collections', label: t('owner.tabs.collections') },
    { value: 'inventory', label: t('owner.tabs.inventory') },
    { value: 'projects', label: t('owner.tabs.projects') },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('owner.title')}</Typography.Title>
        {PERIOD_TABS.has(tab) && (
          <PeriodFilter preset={preset} period={period} onPreset={onPreset} onCustom={setPeriod} />
        )}
      </div>

      <Segmented value={tab} onChange={(v) => setTab(v as string)} options={tabs} />

      <div>
        {tab === 'overview' && <OverviewTab period={period} />}
        {tab === 'cashflow' && <CashFlowTab period={period} />}
        {tab === 'sales' && <SalesTab period={period} />}
        {tab === 'collections' && <CollectionsTab period={period} />}
        {tab === 'inventory' && <InventoryTab />}
        {tab === 'projects' && <ProjectsTab />}
      </div>
    </Space>
  );
}

function OverviewTab({ period }: { period: Period }) {
  const { t } = useTranslation();
  const { data: summary, isLoading } = useOwnerSummary(period);
  const { data: trend } = useRevenueTrend(12);
  const { data: funnel } = useSalesFunnel();
  const { data: inventory } = useInventoryReport();

  if (isLoading || !summary) return <Skeleton active />;
  const k = summary.kpis;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.contractedRevenue')} value={formatUzs(k.contractedRevenue.value)} deltaPercent={k.contractedRevenue.deltaPercent} /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.collected')} value={formatUzs(k.collected.value)} deltaPercent={k.collected.deltaPercent} tone="good" /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.cashPosition')} value={formatUzs(k.cashPosition.value)} tone="good" /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.expenses')} value={formatUzs(k.expenses.value)} deltaPercent={k.expenses.deltaPercent} tone="bad" /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.unitsSold')} value={k.unitsSold.value} deltaPercent={k.unitsSold.deltaPercent} /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.outstanding')} value={formatUzs(k.outstanding.value)} /></Col>
        <Col xs={12} md={6}><KpiCard label={t('owner.kpi.overdue90plus')} value={formatUzs(k.overdue90plus.value)} tone={BigInt(k.overdue90plus.value) > 0n ? 'bad' : 'neutral'} /></Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={t('owner.charts.revenueTrend')}>
            {trend ? <RevenueTrendChart buckets={trend.buckets} labels={{ contracted: t('owner.kpi.contractedRevenue'), collected: t('owner.kpi.collected') }} /> : <Skeleton active />}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t('owner.charts.inventory')}>
            {inventory ? <InventoryPie countLabel={t('owner.inventory.count')} byStatus={inventory.byStatus.map((s) => ({ status: t(`apartments.status.${s.status}`), count: s.count }))} /> : <Skeleton active />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t('owner.charts.funnel')}>
            {funnel ? <SalesFunnelChart stages={funnel.stages.map((s) => ({ name: t(`owner.funnel.${s.key}`), count: s.count }))} /> : <Skeleton active />}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

function ExportButton({ path, file }: { path: string; file: string }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  return (
    <Button icon={<DownloadOutlined />} onClick={async () => {
      try { await downloadFromApi(path, file); } catch (err) { void message.error(asApiError(err).message); }
    }}>
      {t('owner.exportCsv')}
    </Button>
  );
}

function CashFlowTab({ period }: { period: Period }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCashFlowStatement(period);
  if (isLoading || !data) return <Skeleton active />;
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}><Card><Statistic title={t('owner.cashflow.opening')} value={formatUzs(data.opening)} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={t('owner.cashflow.income')} value={formatUzs(data.totalIncome)} valueStyle={{ color: '#16a34a' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={t('owner.cashflow.expense')} value={formatUzs(data.totalExpense)} valueStyle={{ color: '#dc2626' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={t('owner.cashflow.closing')} value={formatUzs(data.closing)} valueStyle={{ color: '#2563eb' }} /></Card></Col>
      </Row>
      <Card title={t('owner.tabs.cashflow')} extra={<ExportButton path={`/reports/owner/cash-flow.csv?${qs(period)}`} file="cash-flow.csv" />}>
        <CashFlowChart income={data.income} expense={data.expense} labels={{ income: t('owner.cashflow.income'), expense: t('owner.cashflow.expense') }} />
      </Card>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title={t('owner.cashflow.income')} bodyStyle={{ padding: 0 }}>
            <Table size="small" rowKey="category" pagination={false} dataSource={data.income}
              columns={[{ title: t('kassa.category'), dataIndex: 'category' }, { title: t('owner.cashflow.amount'), dataIndex: 'amount', align: 'right', render: (v: string) => formatUzs(v) }]} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={t('owner.cashflow.expense')} bodyStyle={{ padding: 0 }}>
            <Table size="small" rowKey="category" pagination={false} dataSource={data.expense}
              columns={[{ title: t('kassa.category'), dataIndex: 'category' }, { title: t('owner.cashflow.amount'), dataIndex: 'amount', align: 'right', render: (v: string) => formatUzs(v) }]} />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

function SalesTab({ period }: { period: Period }) {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState<'month' | 'project' | 'layout'>('month');
  const { data, isLoading } = useSalesReport({ ...period, groupBy });
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <Segmented value={groupBy} onChange={(v) => setGroupBy(v as 'month' | 'project' | 'layout')}
          options={[{ value: 'month', label: t('owner.sales.byMonth') }, { value: 'project', label: t('owner.sales.byProject') }, { value: 'layout', label: t('owner.sales.byLayout') }]} />
        <ExportButton path={`/reports/owner/sales.csv?${qs({ ...period, groupBy })}`} file="sales.csv" />
      </div>
      {isLoading || !data ? <Skeleton active /> : (
        <Card bodyStyle={{ padding: 0 }}>
          <Table rowKey="key" pagination={false} dataSource={data.rows}
            columns={[
              { title: t('owner.sales.group'), dataIndex: 'label', render: (v: string) => groupBy === 'layout' ? t(`apartments.layout.${v}`) : v },
              { title: t('owner.sales.units'), dataIndex: 'units', align: 'right' },
              { title: t('owner.sales.total'), dataIndex: 'total', align: 'right', render: (v: string) => formatUzs(v) },
              { title: t('owner.sales.discount'), dataIndex: 'discount', align: 'right', render: (v: string) => formatUzs(v) },
              { title: t('owner.sales.avgPerSqm'), dataIndex: 'avgPricePerSqm', align: 'right', render: (v: string) => formatUzs(v) },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>{t('owner.sales.totalRow')}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{data.totals.units}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">{formatUzs(data.totals.total)}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{formatUzs(data.totals.discount)}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">—</Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>
      )}
    </Space>
  );
}

function CollectionsTab({ period }: { period: Period }) {
  const { t } = useTranslation();
  const months = monthsFromPeriod(period);
  const { data, isLoading } = useCollectionsReport(months);
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title={t('owner.tabs.collections')} extra={<ExportButton path={`/reports/owner/collections.csv?months=${months}`} file="collections.csv" />}>
        {isLoading || !data ? <Skeleton active /> : (
          <CollectionsChart buckets={data.buckets} labels={{ expected: t('owner.collections.expected'), collected: t('owner.collections.collected') }} />
        )}
      </Card>
      {data && (
        <Card bodyStyle={{ padding: 0 }}>
          <Table size="small" rowKey="month" pagination={false} dataSource={data.buckets}
            columns={[
              { title: t('kassa.date'), dataIndex: 'month' },
              { title: t('owner.collections.expected'), dataIndex: 'expected', align: 'right', render: (v: string) => formatUzs(v) },
              { title: t('owner.collections.collected'), dataIndex: 'collected', align: 'right', render: (v: string) => formatUzs(v) },
              { title: t('owner.collections.rate'), dataIndex: 'ratePercent', align: 'right', render: (v: number) => <Tag color={v >= 90 ? 'green' : v >= 60 ? 'gold' : 'red'}>{v}%</Tag> },
            ]} />
        </Card>
      )}
    </Space>
  );
}

function InventoryTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useInventoryReport();
  if (isLoading || !data) return <Skeleton active />;
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={8}><Card><Statistic title={t('owner.inventory.totalUnits')} value={data.total} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title={t('owner.inventory.absorption')} value={`${data.absorptionPercent}%`} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title={t('owner.inventory.stockValue')} value={formatUzs(data.remainingStockValue)} /></Card></Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} lg={10}>
          <Card title={t('owner.charts.inventory')} extra={<ExportButton path="/reports/owner/inventory.csv" file="inventory.csv" />}>
            <InventoryPie countLabel={t('owner.inventory.count')} byStatus={data.byStatus.map((s) => ({ status: t(`apartments.status.${s.status}`), count: s.count }))} />
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title={t('owner.inventory.byLayout')} bodyStyle={{ padding: 0 }}>
            <Table size="small" rowKey="layout" pagination={false} dataSource={data.byLayout}
              columns={[
                { title: t('apartments.layout_'), dataIndex: 'layout', render: (v: string) => t(`apartments.layout.${v}`) },
                { title: t('owner.sales.units'), dataIndex: 'count', align: 'right' },
                { title: t('owner.cashflow.amount'), dataIndex: 'value', align: 'right', render: (v: string) => formatUzs(v) },
              ]} />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}

function ProjectsTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useProjectProfitability();
  return (
    <Card bodyStyle={{ padding: 0 }} extra={<ExportButton path="/reports/project-profitability.csv" file="project-profitability.csv" />} title={t('owner.tabs.projects')}>
      {isLoading || !data ? <Skeleton active style={{ padding: 24 }} /> : (
        <Table rowKey="projectId" pagination={false} dataSource={data}
          columns={[
            { title: t('projects.cols.name'), dataIndex: 'name' },
            { title: t('contracts.title'), dataIndex: 'contracts', align: 'right' },
            { title: t('owner.projects.contracted'), dataIndex: 'contractedTotal', align: 'right', render: (v: string) => formatUzs(v) },
            { title: t('owner.projects.collected'), dataIndex: 'collectedTotal', align: 'right', render: (v: string) => formatUzs(v) },
            { title: t('owner.projects.rate'), dataIndex: 'collectionRatePercent', align: 'right', render: (v: number) => <Tag color={v >= 90 ? 'green' : v >= 60 ? 'gold' : 'red'}>{v}%</Tag> },
            { title: t('owner.kpi.overdue90plus'), dataIndex: 'overdueOutstanding', align: 'right', render: (v: string) => formatUzs(v) },
          ]} />
      )}
    </Card>
  );
}
